import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Popover,
  Fab,
  CircularProgress,
  Collapse,
  Divider,
} from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import CloudUploadRounded from "@mui/icons-material/CloudUploadRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { RelatedLoad, TripPODResponse } from "../../../types/api";
import documentsIcon from "../../../assets/images/icons/documentsGridIcon.svg";
import { loadsCard } from "../style";
import { useTranslation } from "react-i18next";
import { useTripDocument } from "../../../context/TripDocumentContext";
import {
  BlockOutlined,
  CloseRounded,
  DownloadSharp,
  UploadSharp,
} from "@mui/icons-material";
import { cardDocument } from "../style";
import uploadIcon from "../../../assets/images/icons/CTA_alt.svg";
import uploadOutlineIcon from "../../../assets/images/icons/uploadOutline.svg";
import { tripsService } from "../../../api/tripsService/tripsServices";
type Props = {
  relatedLoads: RelatedLoad[];
  index: number;
  filteredDocuments: TripPODResponse[];
  tmsLocationRelatedLoad: string;
  tmsLocation: string;
  setHasFetchedDocuments?: any;
  stopInvPartyName: string | undefined;
  openSidebar: (deliveryIndex: number, loadingTmsLocation: string, action: boolean) => void;
  unloadIndex: number;
  relatedLoadsLength: number;
};

export const RelatedLoadsCardsDocument: React.FC<Props> = ({
  relatedLoads,
  index,
  filteredDocuments,
  tmsLocationRelatedLoad,
  tmsLocation,
  setHasFetchedDocuments,
  stopInvPartyName,
  openSidebar,
  unloadIndex,
  relatedLoadsLength,
}) => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { getDocumentDetails } = useTripDocument();
  const closeSidebar = () => setIsSidebarOpen(false);

  const handleOpenFilePreview = async (documentId: string) => {
    try {
      const documentDetails = await getDocumentDetails(documentId);
      const pdfBlob = new Blob([documentDetails], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (error) {
      console.error(
        "Errore durante il recupero o la visualizzazione del documento:",
        error
      );
    }
  };

  const documentsByLoad = relatedLoads.map((load, loadIndex) => {
    const relatedDocs = filteredDocuments.filter((doc) => {
      const docLocation = doc.TESTO246_136;
      return docLocation === tmsLocationRelatedLoad + "_" + tmsLocation;
    });
    const documentStatus = relatedDocs[0]?.STATO;
    const cardStatus =
      documentStatus === "Profilo Parziale" ||
      documentStatus === "In Verifica" ||
      documentStatus === "In Gestione" ||
      documentStatus === "Rejected"
        ? "to be evaluated"
        : documentStatus === "Gestito" ||
          documentStatus === "Verificata" ||
          documentStatus === "Approvato"
        ? "accepted"
        : documentStatus === "Annullato" || documentStatus === "Errore"
        ? "rejected"
        : "to be uploaded";

    return { relatedDocs, cardStatus };
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCardDetails, setSelectedCardDetails] = useState<any>(null);
  const [notes, setNotes] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  const toggleNoteExpansion = (noteIndex: number) => {
    const newExpandedNotes = new Set(expandedNotes);
    if (newExpandedNotes.has(noteIndex)) {
      newExpandedNotes.delete(noteIndex);
    } else {
      newExpandedNotes.add(noteIndex);
    }
    setExpandedNotes(newExpandedNotes);
  };

 const handleInfoClick = async (
     event: React.MouseEvent<HTMLButtonElement>,
     documentId: number
   ) => {
     setAnchorEl(event.currentTarget); // Imposta subito anchorEl
 
     try {
       setLoading(true);
       setError(null);
 
       const data = await tripsService.getDocumentsNote(documentId);
       if (data.length > 0) {
         setNotes(data); // 👈 Imposta tutte le note invece di solo l'ultima
       } else {
         setNotes([]); // Nessuna nota disponibile
       }
     } catch (err) {
       setError("Errore nel recupero delle note");
       console.error(err);
     } finally {
       setLoading(false);
     }
   };

  const handleInfoClose = () => {
    setAnchorEl(null);
    setSelectedCardDetails(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "card-details-popover" : undefined;

  return (
    <Box sx={loadsCard.wrapper}>
      {relatedLoads.map((load, i) => {
        const { relatedDocs, cardStatus } = documentsByLoad[i];

        return (
          <React.Fragment key={i}>
            <Card
              elevation={1}
              sx={{
                ...cardDocument.card,
                backgroundColor:
                  cardStatus === "to be evaluated"
                    ? "#FAFAFA"
                    : cardStatus === "accepted"
                    ? "#fafafa"
                    : cardStatus === "rejected"
                    ? "#FAFAFA"
                    : cardStatus === "to be uploaded"
                    ? "#FFEAED" // Nuovo colore per il default
                    : "#FAFAFA",
              }}
            >
              <Box sx={cardDocument.box}>
                {relatedDocs.length > 0 && cardStatus !== "rejected" ? (
                  <>
                    <Typography sx={{ ...cardDocument.title }}>
                      {relatedDocs[0].DOCNAME ||
                        relatedDocs[0].ORIGINALE ||
                        "-"}
                    </Typography>

                    <Box sx={loadsCard.box1}>
                      <Typography sx={loadsCard.loadingPoint}>
                        {t("screens.trips.timeline.loadingPoint")}:
                      </Typography>
                      <Typography sx={loadsCard.loadText}>
                        {load.involvedPartyName || "-"}
                      </Typography>
                    </Box>
                    <Fab
                      color="primary"
                      sx={cardDocument.iconButton2}
                      onClick={() =>
                        handleOpenFilePreview(
                          String(relatedDocs[0]?.DOCNUMBER || "")
                        )
                      }
                    >
                      <DownloadSharp sx={{ color: "white" }} />
                    </Fab>

                    <Box sx={cardDocument.box1}>
                      <Box sx={cardDocument.box2}>
                        <Typography sx={loadsCard.loadingPoint}>
                          {t("screens.trips.tripDocuments.uploaded")}
                        </Typography>
                        <Typography sx={cardDocument.dateText}>
                          {relatedDocs[0]?.CREATION_DATE
                            ? `${new Date(
                                relatedDocs[0].CREATION_DATE
                              ).toLocaleDateString("it-IT", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })} ${new Date(
                                relatedDocs[0].CREATION_DATE
                              ).toLocaleTimeString("it-IT", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`
                            : "-"}
                        </Typography>
                      </Box>
                      <Box sx={cardDocument.box2}>
                        <Typography sx={loadsCard.loadingPoint}>
                          {" "}
                          {t("screens.trips.tripDocuments.by")}
                        </Typography>
                        <Typography sx={cardDocument.dateText}>
                          {relatedDocs[0]?.TESTO223_131}
                        </Typography>
                      </Box>
                      <Box sx={cardDocument.box3}>
                        <Chip
                          sx={{
                            backgroundColor:
                              cardStatus === "accepted"
                                ? "#60C589"
                                : "rgb(57 135 226)",

                            ...loadsCard.chips,
                          }}
                          label={
                            <Box sx={loadsCard.labelBox}>
                              {cardStatus === "accepted" ? (
                                <CheckRounded sx={loadsCard.labelIcon} />
                              ) : (
                                <AccessTimeRounded sx={loadsCard.labelIcon} />
                              )}
                              <Typography sx={loadsCard.labelText}>
                                {(() => {
                                  const statusTranslations: Record<
                                    string,
                                    string
                                  > = {
                                    "to be evaluated": t(
                                      "screens.trips.approvalStatusDoc.TO_BE_EVALUATED"
                                    ),
                                    accepted: t(
                                      "screens.trips.approvalStatusDoc.ACCEPTED"
                                    ),
                                    rejected: t(
                                      "screens.trips.approvalStatusDoc.REJECTED"
                                    ),
                                    "to be uploaded": t(
                                      "screens.trips.approvalStatusDoc.TO_BE_UPLOADED"
                                    ),
                                  };

                                  return statusTranslations[cardStatus] || ""; // Fallback per valori non mappati
                                })()}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box sx={cardDocument.iconBox} />
                        {(cardStatus === "accepted" || cardStatus === "rejected") && 
                         relatedDocs[0]?.NOTE !== null &&
                         relatedDocs[0]?.NOTE !== "" && (
                          <IconButton
                            onClick={(event) =>
                              handleInfoClick(event, relatedDocs[0]?.DOCNUMBER??-1)
                            }
                          >
                            <img src={documentsIcon} alt="Documents Icon" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Box sx={cardDocument.box4}>
                    <Box sx={cardDocument.box5}>
                      <Box sx={cardDocument.box6}>
                        <Box />
                        <Typography sx={loadsCard.loadsText}>
                          {cardStatus === "rejected"
                            ? t("screens.trips.tripDocuments.documentRejected")
                            : t(
                                "screens.trips.tripDocuments.podMandatoryUpload"
                              )}
                        </Typography>
                      </Box>

                      <Typography
                        onClick={() => {
                          handleOpenFilePreview(
                            String(relatedDocs[0]?.DOCNUMBER || "")
                          );
                        }}
                        sx={cardDocument.underlinedTitle}
                      >
                        {relatedDocs[0]?.ORIGINALE ||
                          relatedDocs[0]?.DOCNAME ||
                          ""}
                      </Typography>
                      <Box sx={loadsCard.box1}>
                        <Typography sx={loadsCard.text}>
                          {t("screens.trips.timeline.loadingPoint")}:
                        </Typography>
                        <Typography sx={loadsCard.loadText}>
                          {load.involvedPartyName || "-"}
                        </Typography>
                      </Box>

                      <Fab
                        color="primary"
                        onClick={() =>
                          openSidebar(unloadIndex, load.tmsLocation, false)
                        }
                        sx={cardDocument.iconButton2}
                      >
                        <img src={uploadIcon} alt="Upload Icon" />
                      </Fab>
                      <Box sx={cardDocument.box7}>
                        {cardStatus === "rejected" && (
                          <>
                            <Box sx={cardDocument.box2}>
                              <Typography sx={loadsCard.loadingPoint}>
                                {t("screens.trips.tripDocuments.uploaded")}
                              </Typography>
                              <Typography sx={cardDocument.dateText}>
                                {relatedDocs[0]?.CREATION_DATE
                                  ? `${new Date(
                                      relatedDocs[0].CREATION_DATE
                                    ).toLocaleDateString("it-IT", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })} ${new Date(
                                      relatedDocs[0].CREATION_DATE
                                    ).toLocaleTimeString("it-IT", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}`
                                  : "-"}
                              </Typography>
                            </Box>

                            <Box sx={cardDocument.box2}>
                              <Typography sx={loadsCard.loadingPoint}>
                                {" "}
                                {t("screens.trips.tripDocuments.by")}
                              </Typography>
                              <Typography sx={cardDocument.dateText}>
                                {relatedDocs[0]?.TESTO223_131}
                              </Typography>
                            </Box>
                          </>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            marginTop: "8px",
                          }}
                        >
                          <Chip
                            sx={{
                              ...loadsCard.chips1,
                              backgroundColor:
                                cardStatus === "accepted"
                                  ? "#60C589"
                                  : cardStatus === "rejected" 
                                  ? "primary.main" : cardStatus === "to be uploaded"? "rgb(234, 189, 17)"
                                  : "rgb(57 135 226)",
                            }}
                            label={
                              <Box sx={loadsCard.labelBox}>
                                {cardStatus === "to be uploaded" ? (
                                  <img
                                    src={uploadOutlineIcon}
                                    alt="Upload Icon"
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                ) : (
                                  <BlockOutlined
                                    sx={{ color: "white", fontSize: "14px" }}
                                  />
                                )}

                                <Typography sx={loadsCard.text2}>
                                  {(() => {
                                    const statusTranslations: Record<
                                      string,
                                      string
                                    > = {
                                      "to be evaluated": t(
                                        "screens.trips.approvalStatusDoc.TO_BE_EVALUATED"
                                      ),
                                      accepted: t(
                                        "screens.trips.approvalStatusDoc.ACCEPTED"
                                      ),
                                      rejected: t(
                                        "screens.trips.approvalStatusDoc.REJECTED"
                                      ),
                                      "to be uploaded": t(
                                        "screens.trips.approvalStatusDoc.TO_BE_UPLOADED"
                                      ),
                                    };

                                    return statusTranslations[cardStatus] || ""; // Fallback per valori non mappati
                                  })()}
                                </Typography>
                              </Box>
                            }
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              columnGap: "16px",
                            }}
                          >
                            {(cardStatus === "accepted" || cardStatus === "rejected") && (
                              <Box sx={cardDocument.documentButton} />
                            )}
                            {(cardStatus === "accepted" || cardStatus === "rejected") && 
                             relatedDocs[0]?.NOTE !== null &&
                             relatedDocs[0]?.NOTE !== "" && (
                              <IconButton
                                onClick={(event) =>
                                  handleInfoClick(event, relatedDocs[0]?.DOCNUMBER??-1)
                                }
                                sx={{ padding: 0, margin: 0 }}
                              >
                                <img
                                  src={documentsIcon}
                                  alt="Documents Icon"
                                />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Card>

              <Popover
                       id={popoverId}
                       open={open}
                       anchorEl={anchorEl}
                       onClose={handleInfoClose}
                       anchorOrigin={{
                         vertical: "top",
                         horizontal: "right",
                       }}
                       transformOrigin={{
                         vertical: "top",
                         horizontal: "left",
                       }}
                       slotProps={{
                         paper: {
                           sx: {
                             width: 400,
                             minHeight: 300,
                             maxHeight: 300,
                             overflowY: "auto",
                             borderWidth: "1px",
                             borderColor: "primary.main",
                             borderRadius: "10px",
                           },
                         },
                       }}
                     >
                       <Box
                         sx={{
                           padding: 1.5,
                           height: "100%",
                           position: "relative",
                         }}
                       >
                         {/* IconButton per chiudere il Popover */}
                         <IconButton
                           onClick={handleInfoClose}
                           sx={{
                             position: "absolute",
                             top: 4,
                             right: 4,
                           }}
                         >
                           <CloseRounded />
                         </IconButton>
         
                         <Typography
                           variant="h6"
                           sx={{ mb: 1.5, fontWeight: "bold", fontSize: "18px" }}
                         >
                           {t("screens.trips.notesCard.notes")}
                         </Typography>
         
                         {loading ? (
                           <Box
                             sx={{
                               flex: 1,
                               display: "flex",
                               justifyContent: "center",
                               alignItems: "center",
                             }}
                           >
                             <CircularProgress size={24} />
                           </Box>
                         ) : error ? (
                           <Typography color="error">{error}</Typography>
                         ) : (
                           <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                             {Array.isArray(notes) && notes.length > 0 ? (
                               notes.slice().reverse().map((note: any, index: number) => {
                                 const isExpanded = expandedNotes.has(index);
                                 const shouldShowExpand = note.comment.length > 100;
                                 const displayText = shouldShowExpand && !isExpanded 
                                   ? note.comment.substring(0, 100) + '...' 
                                   : note.comment;

                                 return (
                                   <Box key={note.id || index}>
                                     <Box sx={{ 
                                       display: 'flex', 
                                       justifyContent: 'space-between', 
                                       alignItems: 'flex-start',
                                       py: 1
                                     }}>
                                       <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                         {t("screens.trips.notesCard.note")} {index + 1}
                                       </Typography>
                                       {shouldShowExpand && (
                                         <IconButton
                                           size="small"
                                           onClick={() => toggleNoteExpansion(index)}
                                           sx={{ padding: 0, margin: 0 }}
                                         >
                                           {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                         </IconButton>
                                       )}
                                     </Box>
                                     <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pb: 1 }}>
                                       {displayText}
                                     </Typography>
                                     {index < notes.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                   </Box>
                                 );
                               })
                             ) : (
                               <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                 {t("screens.trips.notesCard.noNotesAvailable")}
                               </Typography>
                             )}
                           </Box>
                         )}
                       </Box>
                     </Popover>
          </React.Fragment>
        );
      })}
    </Box>
  );
};
